import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarPlus, Camera, Clock, FileText, Loader2, LogIn, LogOut, WifiOff } from "lucide-react";
import { api, apiError, inr, logError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const QUEUE_KEY = "pramsia-attendance-queue";

const readQueue = () => {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
};
const writeQueue = (q) => localStorage.setItem(QUEUE_KEY, JSON.stringify(q));

async function getLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  });
}

export default function MyHub() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(readQueue().length);
  const [leave, setLeave] = useState({ leave_type: "Casual Leave", from_date: "", to_date: "", reason: "" });
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const load = useCallback(() => {
    api.get("/hr/my/workspace").then(({ data: d }) => setData(d))
      .catch((e) => logError("my workspace", e));
  }, []);

  const syncQueue = useCallback(async () => {
    const queue = readQueue();
    if (!queue.length || !navigator.onLine) return;
    const left = [];
    for (const item of queue) {
      try {
        await api.post(`/hr/attendance/${item.kind}`, item.payload);
      } catch (e) {
        if (!String(apiError(e)).includes("already")) left.push(item);
      }
    }
    writeQueue(left);
    setQueued(left.length);
    if (queue.length !== left.length) {
      toast.success(`${queue.length - left.length} offline record(s) synced`);
      load();
    }
  }, [load]);

  useEffect(() => {
    load();
    syncQueue();
    const on = () => { setOnline(true); syncQueue(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [load, syncQueue]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      toast.error("Camera not available — attendance will be marked without a selfie");
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const grabSelfie = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return "";
    const canvas = document.createElement("canvas");
    canvas.width = 480;
    canvas.height = Math.round((video.videoHeight / video.videoWidth) * 480);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
  };

  const mark = async (kind) => {
    setBusy(kind);
    try {
      const gps = await getLocation();
      const payload = {
        ...(gps || {}),
        client_ref: `${kind}-${new Date().toISOString().slice(0, 10)}-${Date.now()}`,
        device: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop",
      };
      if (kind === "check-in") payload.selfie_base64 = grabSelfie();
      if (!navigator.onLine) {
        const queue = [...readQueue(), { kind, payload: { ...payload, offline_at: new Date().toISOString() } }];
        writeQueue(queue);
        setQueued(queue.length);
        toast.success("Saved offline — it will sync when you are back online");
      } else {
        const { data: res } = await api.post(`/hr/attendance/${kind}`, payload);
        toast.success(kind === "check-in"
          ? `Checked in${res.face_status === "verified" ? " • face verified" : ""}`
          : "Checked out");
        load();
      }
      stopCamera();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const applyLeave = async () => {
    try {
      await api.post("/hr/leave", leave);
      toast.success("Leave request sent");
      setLeave({ leave_type: "Casual Leave", from_date: "", to_date: "", reason: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const today = data.today;

  return (
    <div>
      <PageHeader testId="my-header" title={`Hello, ${data.me.name.split(" ")[0]}`}
        subtitle={`${data.me.designation || data.me.role} • shift ${data.shift.start}–${data.shift.end}`}
        actions={
          <div className="flex items-center gap-2 text-xs">
            {online ? <Badge variant="outline" data-testid="my-online-badge">online verified</Badge>
              : <Badge variant="destructive" className="gap-1" data-testid="my-offline-badge">
                  <WifiOff className="h-3 w-3" /> offline
                </Badge>}
            {queued > 0 && (
              <Badge className="gap-1" data-testid="my-queue-badge">{queued} pending sync</Badge>
            )}
          </div>
        } />

      <div className="grid gap-3 px-4 py-4 sm:grid-cols-3 sm:px-6">
        <div className="kpi-card" data-testid="my-stat-present">
          <div className="overline">Present days (month)</div>
          <div className="mt-2 font-mono-num text-2xl font-bold">{data.present_days}</div>
        </div>
        <div className="kpi-card" data-testid="my-stat-ot">
          <div className="overline">Overtime hours</div>
          <div className="mt-2 font-mono-num text-2xl font-bold">{data.ot_hours}</div>
        </div>
        <div className="kpi-card" data-testid="my-stat-advance">
          <div className="overline">Advance balance</div>
          <div className="mt-2 font-mono-num text-2xl font-bold">
            {inr(data.advances.reduce((s, a) => s + Number(a.balance || 0), 0))}
          </div>
        </div>
      </div>

      <Tabs defaultValue="attendance" className="px-4 pb-10 sm:px-6">
        <TabsList>
          <TabsTrigger value="attendance" data-testid="my-tab-attendance">
            <Clock className="mr-1.5 h-3.5 w-3.5" /> Attendance
          </TabsTrigger>
          <TabsTrigger value="leave" data-testid="my-tab-leave">
            <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Leave
          </TabsTrigger>
          <TabsTrigger value="payslips" data-testid="my-tab-payslips">
            <FileText className="mr-1.5 h-3.5 w-3.5" /> Payslips
          </TabsTrigger>
        </TabsList>

        <TabsContent value="attendance">
          <div className="rounded-lg border border-border bg-card p-4" data-testid="my-attendance-card">
            {today ? (
              <div className="text-sm">
                Checked in at <b>{String(today.check_in).slice(11, 16)}</b>
                {today.check_out ? <> • checked out at <b>{String(today.check_out).slice(11, 16)}</b></>
                  : " • not checked out yet"}
                <div className="mt-1 text-xs text-muted-foreground">
                  face {today.face_status} • {today.gps ? "location recorded" : "no location"} •
                  {" "}{today.sync_state === "offline_pending_sync" ? "offline pending sync" : "online verified"}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">You have not marked attendance today.</p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <video ref={videoRef} muted playsInline className="h-28 w-36 rounded-lg bg-secondary object-cover"
                data-testid="my-selfie-preview" />
              <Button size="sm" variant="outline" className="gap-1.5" data-testid="my-camera-button"
                onClick={startCamera}>
                <Camera className="h-3.5 w-3.5" /> Open camera
              </Button>
              {!today && (
                <Button size="sm" className="gap-1.5" data-testid="my-checkin-button" disabled={busy === "check-in"}
                  onClick={() => mark("check-in")}>
                  {busy === "check-in" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogIn className="h-3.5 w-3.5" />}
                  Check in
                </Button>
              )}
              {today && !today.check_out && (
                <Button size="sm" className="gap-1.5" data-testid="my-checkout-button" disabled={busy === "check-out"}
                  onClick={() => mark("check-out")}>
                  {busy === "check-out" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
                  Check out
                </Button>
              )}
            </div>
          </div>

          <div className="mt-4" data-testid="my-attendance-history">
            {data.attendance.map((a) => (
              <div key={a.id} className="flex items-center justify-between border-b border-border py-2 text-sm">
                <span>{a.date} • {a.status}</span>
                <span className="text-xs text-muted-foreground">
                  {String(a.check_in || "").slice(11, 16)}–{String(a.check_out || "").slice(11, 16) || "…"}
                  {a.ot_hours ? ` • OT ${a.ot_hours}h` : ""}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="leave">
          <div className="flex flex-wrap items-end gap-2" data-testid="my-leave-form">
            <Select value={leave.leave_type} onValueChange={(v) => setLeave({ ...leave, leave_type: v })}>
              <SelectTrigger className="w-44" data-testid="my-leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Casual Leave", "Sick Leave", "Earned Leave", "Paid Leave", "Unpaid Leave"].map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" className="w-40" data-testid="my-leave-from" value={leave.from_date}
              onChange={(e) => setLeave({ ...leave, from_date: e.target.value })} />
            <Input type="date" className="w-40" data-testid="my-leave-to" value={leave.to_date}
              onChange={(e) => setLeave({ ...leave, to_date: e.target.value })} />
            <Input className="w-52" placeholder="Reason" data-testid="my-leave-reason" value={leave.reason}
              onChange={(e) => setLeave({ ...leave, reason: e.target.value })} />
            <Button size="sm" data-testid="my-leave-submit" disabled={!leave.from_date || !leave.to_date}
              onClick={applyLeave}>Apply</Button>
          </div>
          <div className="mt-4" data-testid="my-leave-list">
            {data.leaves.map((l) => (
              <div key={l.id} className="flex items-center justify-between border-b border-border py-2 text-sm">
                <span>{l.leave_type} • {l.from_date} → {l.to_date} • {l.days} day(s)</span>
                <Badge variant={l.status === "Approved" ? "outline" : l.status === "Rejected" ? "destructive" : "default"}>
                  {l.status}
                </Badge>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="payslips" data-testid="my-payslips">
          {data.payslips.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">No payslips yet.</p>
          ) : (
            data.payslips.map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-border py-2 text-sm">
                <span>{p.month}</span>
                <span className="flex items-center gap-3">
                  <span className="font-mono-num">{inr(p.line?.net_pay)}</span>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/payroll/payslips/${p.id}/pdf`}
                    target="_blank" rel="noreferrer" className="text-xs underline"
                    data-testid={`my-payslip-pdf-${p.id}`}>
                    Download PDF
                  </a>
                </span>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
